import { memo, useId } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { HStack, SearchField, FilterDropdown, Button } from '@library';
import type { Bicycle } from '../../types';
import type { BicycleFilters } from '../BicycleSearch';
import getBicycleFilterValues from '../../util/getBicycleFilterValues';
import styles from './SparePartsFilters.module.scss';

const SparePartsSelect = memo(
    ({
        options,
        values,
        onChange,
        placeholderId,
        ...ariaProps
    }: {
        options: string[];
        values: string[];
        onChange: (value: string[]) => void;
        placeholderId: string;
    }) => {
        const optionsWithLabel = options
            .filter(valueForFilter => valueForFilter !== '')
            .map((item: string) => ({
                label: item,
                value: item,
                search: item.replaceAll('-', ' '),
            }));

        const [value] = values;
        const id = useId();
        const intl = useIntl();

        return optionsWithLabel.length ? (
            <div>
                <FilterDropdown
                    searchable
                    id={id}
                    options={optionsWithLabel}
                    value={value}
                    placeholder={intl.formatMessage({ id: placeholderId })}
                    onChange={changed => {
                        onChange([changed]);
                    }}
                    windowRender={{ height: 225 }}
                    {...ariaProps}
                />
            </div>
        ) : null;
    }
);

const SparePartsDropdownFilters = memo(
    ({
        bicycles,
        filters,
        setFilters,
    }: {
        bicycles: Bicycle[];
        filters: BicycleFilters;
        setFilters: (value: BicycleFilters) => void;
    }) => {
        const validFilterValues = getBicycleFilterValues(bicycles, filters);

        const dropdowns: {
            name: keyof typeof filters;
            dataTestClass: string;
            placeholderId: string;
        }[] = [
            {
                name: 'make',
                dataTestClass: 'makeSelect',
                placeholderId: 'make',
            },
            {
                name: 'model',
                dataTestClass: 'modelSelect',
                placeholderId: 'model',
            },
            {
                name: 'category',
                dataTestClass: 'categorySelect',
                placeholderId: 'category',
            },
            {
                name: 'type',
                dataTestClass: 'typeSelect',
                placeholderId: 'product type',
            },
            {
                name: 'modelYear',
                dataTestClass: 'modelYearSelect',
                placeholderId: 'model year',
            },
        ];

        return (
            <>
                {dropdowns.map(({ name, dataTestClass, ...props }) => (
                    <SparePartsSelect
                        key={name}
                        data-test-class={dataTestClass}
                        values={filters[name]}
                        options={validFilterValues[name]}
                        {...props}
                        onChange={value => {
                            setFilters({ ...filters, [name]: value });
                        }}
                    />
                ))}
            </>
        );
    }
);

const SparePartsFilters = ({
    search,
    setSearch,
    bicycles,
    filters,
    setFilters,
    resetFilters,
    areFiltersActive,
}: {
    search: string;
    setSearch: (value: string) => void;
    bicycles: Bicycle[];
    filters: BicycleFilters;
    setFilters: (value: BicycleFilters) => void;
    resetFilters: () => void;
    areFiltersActive: boolean;
}) => {
    return (
        <HStack gap="small" wrap>
            <div className={styles.searchField}>
                <SearchField
                    value={search}
                    onChange={setSearch}
                    data-test-class="vehicleFastSearchInput"
                />
            </div>

            <SparePartsDropdownFilters
                bicycles={bicycles}
                filters={filters}
                setFilters={setFilters}
            />

            {areFiltersActive && (
                <Button
                    text
                    onClick={resetFilters}
                    data-test-class="resetFilters"
                >
                    <FormattedMessage id="reset filter" />
                </Button>
            )}
        </HStack>
    );
};

export default memo(SparePartsFilters);
